import { Inject, Injectable, InjectionToken } from '@angular/core';
import { EmptyEnvironment, Environment, Environments } from '@core/constants';

export const getWindow = (): Window => {
  return window;
};

export const WINDOW = new InjectionToken<Window>('BrowserGlobalNamespace', {
  providedIn: 'root',
  factory: getWindow,
});

@Injectable({
  providedIn: 'root'
})
export class WindowService {
  public readonly document: Document = this.window.document;
  private readonly location: Location = this.window.location;

  public get environment(): Environment {
    const hostName = this.getHostName().toLowerCase();
    return Environments.find((env) => {
      return hostName.startsWith(`${env.Key}.`) || hostName.startsWith(`${env.Key}-`);
    }) || EmptyEnvironment;
  }

  constructor(@Inject(WINDOW) public readonly window: Window & typeof globalThis) { }

  public setUrl(path: string, origin?: string): void {
    if (path.charAt(0) === '/' || path.charAt(0) === '\\') {
      path = path.slice(1);
    }

    if (origin) {
      // TODO(wayland): Build white list of allowed domains/origins. Throw error
      // if origin doesn't match any
      this.location.href = origin;
      return;
    } else {
      origin = this.location.origin;
    }

    this.location.href = `${origin}/${path}`;
  }

  public setLocation(url: string): void {
    this.location.href = this.toAbsoluteUrl(url);
  }

  public getHost() {
    return this.location.host;
  }

  public getHostName() {
    return this.location.hostname;
  }

  public encodeUri(uri: string): string {
    return this.window.encodeURIComponent(uri);
  }

  /**
   * Converts a user-provided URL into a valid absolute URL.
   *
   * If the input already contains a protocol (e.g. `https://example.com`),
   * it is returned as-is. If the protocol is missing (e.g. `example.com`
   * or `www.example.com`), `https://` is automatically prepended.
   *
   * This normalization is required because browsers treat protocol-less
   * URLs as relative paths when assigned to `window.location.href`.
   *
   * @param url - A user-provided URL that may or may not include a protocol.
   * @returns A fully qualified absolute URL safe to use for navigation.
   */
  private toAbsoluteUrl(url: string): string {
    try {
      return new URL(url).href;
    } catch {
      return new URL(`https://${url}`).href;
    }
  }
}
